import i18n from '@/utils/i18n';
import React from 'react';
import { AddressInputType } from '@domain/addresses/types/types';
import { gql } from '@apollo/client';
import { SelectInput } from '@/components/select-input';
import { StyleSheet } from 'react-native';
import { TextInput, Props as TextInputProps } from '@/components/text-input';

export const ADDRESS_FIELDS_FRAGMENT = gql`
  fragment AddressInputFields on addresses {
    address
    address2
    city
    country_code
    latitude
    longitude
    region
    uuid
  }
`;

export const getAddressComponentValue = (
  components: google.maps.GeocoderAddressComponent[] = [],
  type: string,
  style: 'long_name' | 'short_name' = 'long_name',
): string => {
  const component = components.find((c) => c.types.includes(type));
  return component?.[style] || '';
};

export const getAddressInputFromPlaceResult = (
  details: google.maps.places.PlaceResult,
  address2?: string,
): AddressInputType => {
  const {
    address_components: components = [],
    formatted_address: address = '',
    geometry,
  } = details;

  const location = geometry?.location as unknown as google.maps.LatLngLiteral;
  return {
    address,
    address2:
      address2 ||
      getAddressComponentValue(components, 'subpremise') ||
      undefined,
    city:
      getAddressComponentValue(components, 'locality') ||
      getAddressComponentValue(components, 'sublocality'),
    countryCode: getAddressComponentValue(components, 'country', 'short_name'),
    latitude: location.lat,
    longitude: location.lng,
    region: getAddressComponentValue(components, 'administrative_area_level_1'),
  };
};

type ModifiedInputProps = Omit<TextInputProps, 'label'>;

export interface Props {
  address2: string;
  address2Label?: string;
  address2Props?: Partial<ModifiedInputProps>;
  addressProps?: Partial<ModifiedInputProps>;
  label?: string;
  onAddress2Change: (newValue: string) => void;
  onAddressChange?: (address: AddressInputType | null) => void;
  onOpenSearch: () => void;
  useOuterLabel?: boolean;
  value: AddressInputType | null;
}

export const AddressInput: React.FC<Props> = ({
  address2,
  address2Label = i18n.t('address_2'),
  address2Props = {},
  addressProps = {},
  label = i18n.t('address'),
  onAddressChange,
  onAddress2Change,
  onOpenSearch,
  useOuterLabel,
  value,
}) => {
  return (
    <>
      <SelectInput
        label={label}
        onPress={onOpenSearch}
        useOuterLabel={useOuterLabel}
        value={value?.address || ''}
        {...addressProps}
      />
      <TextInput
        containerStyle={styles.address2}
        label={address2 && !useOuterLabel ? address2Label : i18n.t('address_2')}
        onChangeText={(text) => {
          onAddress2Change(text);

          if (value && onAddressChange) {
            onAddressChange({
              ...value,
              address2: text,
            });
          }
        }}
        placeholder={i18n.t('address_2')}
        useOuterLabel={useOuterLabel}
        value={address2}
        {...address2Props}
      />
    </>
  );
};

const styles = StyleSheet.create({
  address2: {
    marginTop: 4,
  },
});
